<script setup lang="ts">
import Button from "../../../Button.vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { computed, ref } from "vue";
import { REPORT_CARD_OPTIONS } from "../types";
import { useStore } from "vuex";
import TextAreaOption from "../components/TextAreaOptionBtn.vue";
import OptionBtn from "../components/OptionBtn.vue";
import CreditLineTypes from "@/apps/credit-line/store/types";
import { commitForCreditLine } from "../../../../helpers/general.helper";
import { getActivePhysicalCard } from "@/apps/credit-line/helpers/getActivePhysicalCard";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";

const store = useStore();
const { t } = useI18n();
const reportCardOptions =
  store.getters["creditLine/getReportLostOrDamagedCard"];
const currentOption = ref<string>(reportCardOptions?.reason || "");
const detailsReport = ref<string>(reportCardOptions?.details || "");
const { summary, fetchSummary } = useCreditLineSummary();
fetchSummary(true);

const cardReportOptions = {
  [REPORT_CARD_OPTIONS.LOST]: t("REPORT_LOST_OR_STOLEN_CARD.VIEW_2.OPTION_1"),
  [REPORT_CARD_OPTIONS.BROKEN]: t("REPORT_LOST_OR_STOLEN_CARD.VIEW_2.OPTION_2"),
  [REPORT_CARD_OPTIONS.STOLEN]: t("REPORT_LOST_OR_STOLEN_CARD.VIEW_2.OPTION_3"),
};

const emit = defineEmits(["next", "back"]);

const canContinueBtn = computed(() => {
  if (currentOption.value === REPORT_CARD_OPTIONS.CLIENT_INTERNAL_REASON) {
    const reportText = detailsReport.value.trim();
    return reportText.length > 3;
  }
  return !!currentOption.value;
});

const handleContinueClick = () => {
  const activePhysicalCard = getActivePhysicalCard(summary.value);
  const isOtherOptionSelected =
    currentOption.value === REPORT_CARD_OPTIONS.CLIENT_INTERNAL_REASON;
  const payload = {
    reason: currentOption.value,
    details: isOtherOptionSelected ? detailsReport.value : "",
    lastFour: activePhysicalCard?.lastFour || 0,
  };
  commitForCreditLine(CreditLineTypes.SET_CARD_LOST_OR_DAMAGED, payload, store);
  emit("next");
};
const handleBackClick = () => {
  emit("back");
};
const handleClickOtherOption = () => {
  currentOption.value = REPORT_CARD_OPTIONS.CLIENT_INTERNAL_REASON;
};
</script>

<template>
  <div>
    <div class="header">
      <button class="header-btn" @click="handleBackClick">
        <i class="fa-solid fa-arrow-left"></i>
      </button>
      <p class="title">{{ t("REPORT_LOST_OR_STOLEN_CARD.VIEW_2.TITLE") }}</p>
    </div>
    <div class="body">
      <div>
        <p class="body-title">
          {{ t("REPORT_LOST_OR_STOLEN_CARD.VIEW_2.TITLE") }}
        </p>
        <p class="body-subtitle">
          {{ t("REPORT_LOST_OR_STOLEN_CARD.VIEW_2.DESCRIPTION") }}
        </p>
      </div>
      <div class="btn-options-container">
        <OptionBtn
          v-for="(item, key) in cardReportOptions"
          :key="item"
          :label="item"
          :isSelected="currentOption === key"
          @click="currentOption = key"
        />
        <TextAreaOption
          v-model="detailsReport"
          :placeholder="t('REPORT_LOST_OR_STOLEN_CARD.VIEW_2.PLACEHOLDER')"
          :isSelected="
            currentOption === REPORT_CARD_OPTIONS.CLIENT_INTERNAL_REASON
          "
          @click="handleClickOtherOption"
          :label="t('REPORT_LOST_OR_STOLEN_CARD.VIEW_2.OPTION_4')"
        />
      </div>
    </div>
    <div class="footer">
      <Button
        :class="[!canContinueBtn && 'btn-disabled']"
        @click="handleContinueClick"
        >{{ t("REPORT_LOST_OR_STOLEN_CARD.VIEW_2.CTA") }}</Button
      >
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

.header-btn {
  background: none;
  border: none;
}
.header {
  display: flex;
  align-items: center;
  padding: 20px;
  width: 100%;
  border-bottom: 1px solid #f2f2f2;
}
.fa-arrow-left {
  font-size: 20px;
}
.title {
  margin: 0px;
  padding-left: 12px;
  font-size: 20px;
  font-weight: 500;
  font-size: 14px;
  line-height: 21px;
  color: #111111;
}
.body {
  padding: 20px;
  width: 100%;
}
.body-title {
  color: #111111;
  font-size: 20px;
  font-weight: 600;
  line-height: 29px;
  margin: 21px 0px 0px 0px;
}
.body-subtitle {
  line-height: 21px;
  font-size: 14px;
  font-weight: 500;
  color: #595a5a;
  margin: 8px 0px 0px 0px;
}
.footer {
  padding: 20px;
  margin-top: 30px;
}
.btn-disabled {
  opacity: 0.8;
}
</style>
